import { useMemo, useState, useRef } from "react";
import { makeStyles } from "@mui/styles";
import TextField from "@mui/material/TextField";
import { useDispatch, useSelector } from "react-redux";
import styled from "@emotion/styled";
import KeyBoardInstance from "@/utils/KeyBoardBounce";
import OutlinedInput from "@mui/material/OutlinedInput";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import { Navbar } from "./navBar";
import { updateEditValue } from "@/store/slices/waitlist";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@/utils";
import { BigButton } from "@/components/button";
import PhoneNumberTextMask from "../../components/phoneNumberTextMask";
import { useGlobalState } from "@/hooks/useGlobalState";
import { setLoading } from "@/store/slices/feedback";
import { createNewWait } from "@/apis";
import Message from "@/components/Message";
import useCRM from "@/hooks/useCRM";

const useStyles = makeStyles({
  root: {
    display: "flex",
    alignItems: "center",
    flexDirection: "column",
    textAlign: "center",
    color: "#4a4f54",
  },
  question: {
    fontSize: 36,
    fontWeight: 500,
    marginBottom: 16,
  },
  input: {
    margin: "50px",
    width: 377,
  },
  helperText: {
    fontSize: 24,
  },
  subText: {
    fontSize: 16,
    color: "#999",
    marginBottom: 16,
  },
});

const YellowTextField = styled(TextField)({
  "& label.Mui-focused": {
    color: "#ffcc33",
  },
  "& .MuiInput-underline:after": {
    borderBottomColor: "#ffcc33",
  },
  "& .MuiOutlinedInput-root": {
    // "& .MuiInputBase-input::-webkit-input-placeholder": {
    //   fontSize: "18px",
    // },
    "& fieldset": {
      borderColor: "#ffcc33",
    },
    "&:hover fieldset": {
      borderColor: "#ffcc33",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#ffcc33",
    },
  },
});

const YellowFormControl = styled(FormControl)({
  "& label.Mui-focused": {
    color: "#ffcc33",
  },
  "& .MuiInput-underline:after": {
    borderBottomColor: "#ffcc33",
  },
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: "#ffcc33",
    },
    "&:hover fieldset": {
      borderColor: "#ffcc33",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#ffcc33",
    },
  },
});

export default (props) => {
  const [language] = useGlobalState("language");
  const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { mode } = useQuery();
  const { search } = useLocation();
  const nameTextRef = useRef(null);
  const phoneTextRef = useRef(null);
  const noteTextRef = useRef(null);
  const wrapperRef = useRef(null);
  const newWait = useSelector((state) => state.waitlist.edit.value);
  const [contact, setContact] = useState({ name: "", phone: "", note: "" });
  const [inputErrors, setInputErrors] = useState({});
  const disabled = useSelector((state) => state.waitlist.disabled);
  const { merchantId } = useParams();
  const nextPage = window.location.pathname.replace(/\/[^\/]*$/, "/confirm");
  const backPage = window.location.pathname.replace(/\/[^\/]*$/, "/partySize");
  const startPage = window.location.pathname.replace(/\/[^\/]*$/, "/start");
  const { isCRMOpen, checkCRMMember } = useCRM(merchantId, "front");
  const onChange = (field, value) => {
    setContact({
      ...contact,
      [field]: value,
    });
  };

  const onChangePhone = (ev) => {
    let value = ev.target.value;
    // value = value.replace(/\D/g, "")
    // if (value.length > 10) return;
    // if (value === contact.phone) return;
    setContact({ ...contact, phone: value });
  };

  const onSave = async () => {
    const { name, phone } = contact;
    const errors = {};
    if (!name || name.length === 0) {
      errors.name = "required";
    }
    if (!phone || phone.length === 0) {
      errors.phone = "required";
    } else {
      const onlyNumbers = phone.replace(/\D/g, "");
      if (onlyNumbers.length < 10) {
        errors.phone = "invalid phone number";
      }
    }
    setInputErrors(errors);
    if (errors.name || errors.phone) {
      return;
    }
    dispatch(updateEditValue(contact));
    // navigate(nextPage + search);
    await onJoin(contact);
  };

  const onJoin = async (contact) => {
    try {
      dispatch(setLoading(true));
      const newWaitData = {
        partySize: newWait.partySize,
        ...contact,
        phone: contact.phone.replace(/\D/g, ""),
      };
      const res = await createNewWait(merchantId, newWaitData);
      const { waitId } = res.data;
      let isNavigateToCRM = false;
      // 1. 判断是否开通crm
      if (isCRMOpen) {
        // 2. 判断是否已经是会员
        isNavigateToCRM = !(await checkCRMMember(contact.phone));
      }
      let nextPage = window.location.pathname.replace(
        /\/[^\/]*$/,
        `/${isNavigateToCRM ? "crmLogin" : "waitingNumber"}?id=${waitId}`,
      );
      if (mode) {
        nextPage += `&mode=${mode}`;
      }
      navigate(nextPage);
    } catch (e) {
      if (e?.response) {
        const {
          data: { description },
        } = e.response;
        Message.error({ content: description });
      }
    } finally {
      dispatch(setLoading(false));
    }
  };

  const isShowCn = useMemo(() => {
    return language.includes("zh");
  }, [language]);

  const handleKeyPressEnter = (event) => {
    if (event.key === "Enter" || event.keyCode === 13) {
      onSave();
    }
  };

  return (
    <div className={classes.root}>
      <Navbar
        backUrl={backPage}
        closeUrl={startPage}
        invisible={mode === "kiosk"}
      />
      <div>
        <div className={classes.question}>
          <span>What's your name </span>
          {isShowCn && <span>(姓名)</span>}
          <span>?</span>
        </div>
        <YellowTextField
          inputRef={nameTextRef}
          style={{ marginBottom: 32, fontSize: 28 }}
          classes={{ root: classes.input }}
          inputProps={{
            style: {
              height: 32,
              fontSize: "28px",
            },
          }}
          id="name"
          variant="outlined"
          onChange={(ev) => onChange("name", ev.target.value)}
          helperText={inputErrors["name"]}
          FormHelperTextProps={{
            style: { fontSize: "24px" },
          }}
          error={inputErrors["name"]}
          onFocus={() => {
            KeyBoardInstance.checkIfNeedBounce(nameTextRef.current);
          }}
          onBlur={() => {
            KeyBoardInstance.removeBoxFromBody();
          }}
        />
      </div>
      <div>
        <div className={classes.question}>
          <span>What's your phone number</span>
          {isShowCn && <span>(手机号)</span>}
          <span>?</span>
        </div>
        <YellowFormControl
          style={{ marginBottom: 20 }}
          classes={{ root: classes.input }}
          id="phone"
        >
          <OutlinedInput
            inputRef={phoneTextRef}
            inputProps={{
              style: {
                height: 32,
                fontSize: "28px",
              },
            }}
            value={contact.phone}
            onChange={onChangePhone}
            inputComponent={PhoneNumberTextMask}
            error={inputErrors["phone"]}
            type="tel"
            onFocus={() => {
              KeyBoardInstance.checkIfNeedBounce(phoneTextRef.current);
            }}
            onBlur={() => {
              const removeEffectFn = () => {
                window.scrollTo({
                  top: document.documentElement.scrollHeight, // 滚动到页面的总高度
                  behavior: "smooth", // 平滑滚动
                });
              };
              KeyBoardInstance.removeBoxFromBody(removeEffectFn);
            }}
            onKeyPress={handleKeyPressEnter}
          />
          <FormHelperText style={{ fontSize: "24px", color: "#d32f2f" }}>
            {inputErrors["phone"]}
          </FormHelperText>
        </YellowFormControl>
      </div>
      <div>
        <div className={classes.question} style={{ marginBottom: 0 }}>
          <span>Note</span>
          {isShowCn && <span>(备注)</span>}
        </div>
        <div className={classes.subText}>
          <span>birthday, highchair etc.</span>
          {isShowCn && <span>/生日,高脚椅等</span>}
        </div>
        <YellowTextField
          inputRef={noteTextRef}
          style={{ marginBottom: 32, fontSize: 28 }}
          classes={{ root: classes.input }}
          inputProps={{
            style: {
              height: 32,
              fontSize: "28px",
            },
          }}
          id="note"
          variant="outlined"
          onChange={(ev) => onChange("note", ev.target.value)}
          onFocus={() => {
            KeyBoardInstance.checkIfNeedBounce(noteTextRef.current);
          }}
          onBlur={() => {
            KeyBoardInstance.removeBoxFromBody();
          }}
        />
      </div>
      <BigButton onClick={onSave} mode={mode} disabled={disabled}>
        <span>Next</span>
        {isShowCn && <span>(继续)</span>}
      </BigButton>
    </div>
  );
};
